/**
 * Rendering an attachment: as a tile in the thread, and full size in a dialog.
 *
 * The rule both share is that a file shows *itself*. A generic glyph is a
 * placeholder for a preview that was never written — it tells you a file
 * exists, which the filename already did, and nothing about whether it is the
 * one you are looking for. So an image is an image, a text file is its first
 * lines, a PDF is its first page, and a video is a frame from it.
 *
 * Two libraries do the work a browser cannot:
 *
 *  - **pdf.js** renders a PDF to a canvas. Its worker is imported as a URL so
 *    Vite emits it as a same-origin asset; the default resolution fetches a
 *    worker from a CDN, which our `default-src 'self'` refuses and which would
 *    have failed silently as "PDF preview unavailable".
 *  - **highlight.js** colours code. Its `highlightAuto` is deliberately not
 *    used — guessing the language of a 40-line snippet is unreliable, and a
 *    wrong guess is worse than none because it colours identifiers as keywords.
 *    The extension decides, and an unknown extension is left plain.
 *
 * Both are loaded on demand. A person who never opens a PDF never downloads a
 * PDF renderer, which is most of the point of code-splitting a tool that mostly
 * shows screenshots.
 */

import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Orb } from './orb.js';

import type { BoardAttachmentView } from '../../client/types.js';
import { Button } from '@primer/react';

const IMAGE_TYPES = new Set(['image/png', 'image/jpeg', 'image/gif', 'image/webp', 'image/avif', 'image/svg+xml']);
const VIDEO_TYPES = new Set(['video/mp4', 'video/webm', 'video/ogg', 'video/quicktime']);
const AUDIO_TYPES = new Set(['audio/mpeg', 'audio/ogg', 'audio/wav', 'audio/webm', 'audio/mp4', 'audio/flac']);
const PDF_TYPES = new Set(['application/pdf']);

/** Extension → highlight.js language. An absent entry means "show it plain". */
const LANGUAGES: Readonly<Record<string, string>> = {
  ts: 'typescript',
  tsx: 'typescript',
  js: 'javascript',
  jsx: 'javascript',
  json: 'json',
  yml: 'yaml',
  yaml: 'yaml',
  py: 'python',
  go: 'go',
  rs: 'rust',
  java: 'java',
  sql: 'sql',
  sh: 'bash',
  bash: 'bash',
  zsh: 'bash',
  css: 'css',
  html: 'xml',
  xml: 'xml',
  md: 'markdown',
  toml: 'ini',
  ini: 'ini',
  diff: 'diff',
  patch: 'diff',
};

export type PreviewKind = 'image' | 'video' | 'audio' | 'pdf' | 'text' | 'binary';

function extensionOf(filename: string): string {
  const dot = filename.lastIndexOf('.');
  return dot === -1 ? '' : filename.slice(dot + 1).toLowerCase();
}

function isTextual(mediaType: string, filename: string): boolean {
  if (mediaType.startsWith('text/')) return true;
  if (['application/json', 'application/xml', 'application/x-yaml', 'application/yaml'].includes(mediaType)) return true;
  return /^(txt|log|csv|tsv|conf|env)$/u.test(extensionOf(filename)) || extensionOf(filename) in LANGUAGES;
}

export function previewKind(mediaType: string, filename: string): PreviewKind {
  if (IMAGE_TYPES.has(mediaType)) return 'image';
  if (VIDEO_TYPES.has(mediaType)) return 'video';
  if (AUDIO_TYPES.has(mediaType)) return 'audio';
  if (PDF_TYPES.has(mediaType)) return 'pdf';
  if (isTextual(mediaType, filename)) return 'text';
  return 'binary';
}

export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${String(bytes)} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/* ----------------------------------------------------------------- pdf --- */

/**
 * Renders one page of a PDF to a data URL.
 *
 * The module and its worker are imported here so neither is in the main bundle.
 * `?url` makes Vite emit the worker as an asset and hand back its path, which
 * is same-origin and therefore allowed by the Content-Security-Policy.
 */
async function renderPdfPage(bytes: ArrayBuffer, pageNumber: number, targetWidth: number): Promise<string> {
  const [pdfjs, workerUrl] = await Promise.all([
    import('pdfjs-dist'),
    import('pdfjs-dist/build/pdf.worker.min.mjs?url').then((module) => module.default),
  ]);

  pdfjs.GlobalWorkerOptions.workerSrc = workerUrl;

  const document_ = await pdfjs.getDocument({ data: new Uint8Array(bytes) }).promise;
  const page = await document_.getPage(Math.min(pageNumber, document_.numPages));

  const base = page.getViewport({ scale: 1 });
  const viewport = page.getViewport({ scale: targetWidth / base.width });

  const canvas = document.createElement('canvas');
  canvas.width = Math.ceil(viewport.width);
  canvas.height = Math.ceil(viewport.height);

  const context = canvas.getContext('2d');
  if (context === null) throw new Error('this browser gave no 2D canvas context');

  await page.render({ canvas, canvasContext: context, viewport }).promise;
  return canvas.toDataURL('image/png');
}

/** Total pages, for the viewer's pager. */
async function pdfPageCount(bytes: ArrayBuffer): Promise<number> {
  const pdfjs = await import('pdfjs-dist');
  const document_ = await pdfjs.getDocument({ data: new Uint8Array(bytes) }).promise;
  return document_.numPages;
}

function usePdfPage(blob: Blob | null, page: number, width: number): string | null {
  const [url, setUrl] = useState<string | null>(null);

  useEffect(() => {
    if (blob === null) return undefined;
    let live = true;
    void blob
      .arrayBuffer()
      .then((bytes) => renderPdfPage(bytes, page, width))
      .then((rendered) => {
        if (live) setUrl(rendered);
      })
      .catch(() => {
        // A PDF that will not render is shown as an unpreviewable file rather
        // than as a broken image; it is still downloadable.
        if (live) setUrl(null);
      });
    return () => {
      live = false;
    };
  }, [blob, page, width]);

  return url;
}

/* ---------------------------------------------------------------- code --- */

function useHighlighted(text: string | null, filename: string): string | null {
  const [html, setHtml] = useState<string | null>(null);

  useEffect(() => {
    const language = LANGUAGES[extensionOf(filename)];
    if (text === null || language === undefined) {
      setHtml(null);
      return undefined;
    }

    let live = true;
    void import('highlight.js/lib/common')
      .then((module) => module.default.highlight(text, { language }).value)
      .catch(() => null)
      .then((value) => {
        if (live) setHtml(value);
      });
    return () => {
      live = false;
    };
  }, [text, filename]);

  return html;
}

/**
 * Code, with line numbers.
 *
 * `dangerouslySetInnerHTML` carries highlight.js output only, which is the
 * file's own text with `<span class>` around tokens — it escapes everything
 * else. When highlighting is unavailable the plain text goes through React's
 * normal escaping instead, so an untrusted file can never inject markup either
 * way.
 */
function CodeBlock({ text, filename, lines }: { text: string; filename: string; lines?: number }): ReactNode {
  const shown = lines === undefined ? text : text.split('\n').slice(0, lines).join('\n');
  const html = useHighlighted(shown, filename);
  const count = shown.split('\n').length;

  return (
    <div className="code-block">
      <ol className="code-gutter" aria-hidden="true">
        {Array.from({ length: count }, (_, index) => (
          <li key={index}>{index + 1}</li>
        ))}
      </ol>
      <pre className="code-body">
        {html === null ? <code>{shown}</code> : <code dangerouslySetInnerHTML={{ __html: html }} />}
      </pre>
    </div>
  );
}

/* --------------------------------------------------------------- tiles --- */

export interface LoadedAttachment {
  readonly url: string;
  readonly blob: Blob;
  readonly text: string | null;
}

/** The tile shown inside a comment. Shows the file, not an icon for it. */
export function AttachmentTile({
  attachment,
  loaded,
  onOpen,
}: {
  attachment: BoardAttachmentView;
  loaded: LoadedAttachment | null;
  onOpen: () => void;
}): ReactNode {
  const kind = previewKind(attachment.mediaType, attachment.filename);
  const pdfThumb = usePdfPage(kind === 'pdf' ? loaded?.blob ?? null : null, 1, 320);

  return (
    <button type="button" className={`attachment attachment-${kind}`} onClick={onOpen} title={attachment.filename}>
      <span className="attachment-frame">
        {loaded === null ? (
          <span className="attachment-loading" aria-hidden="true"><Orb size={20} /></span>
        ) : kind === 'image' ? (
          <img className="attachment-thumb" src={loaded.url} alt={attachment.filename} />
        ) : kind === 'video' ? (
          // `preload="metadata"` gets a first frame without pulling the file.
          <video className="attachment-thumb" src={loaded.url} preload="metadata" muted playsInline />
        ) : kind === 'pdf' ? (
          pdfThumb === null ? (
            <span className="attachment-loading" aria-hidden="true"><Orb size={20} /></span>
          ) : (
            <img className="attachment-thumb attachment-thumb-page" src={pdfThumb} alt={`First page of ${attachment.filename}`} />
          )
        ) : kind === 'text' ? (
          <span className="attachment-snippet">{(loaded.text ?? '').split('\n').slice(0, 8).join('\n')}</span>
        ) : kind === 'audio' ? (
          <span className="attachment-glyph" aria-hidden="true">
            ♪
          </span>
        ) : (
          <span className="attachment-glyph" aria-hidden="true">
            ▤
          </span>
        )}
      </span>
      <span className="attachment-name">{attachment.filename}</span>
      <span className="attachment-meta">{formatBytes(attachment.sizeBytes)}</span>
    </button>
  );
}

/* -------------------------------------------------------------- viewer --- */

export function AttachmentDialog({
  attachment,
  loaded,
  onClose,
  position,
  onPrev,
  onNext,
}: {
  attachment: BoardAttachmentView;
  loaded: LoadedAttachment | null;
  onClose: () => void;
  /** This file's place among the card's attachments, when there are others. */
  position?: { readonly index: number; readonly total: number } | undefined;
  onPrev?: (() => void) | undefined;
  onNext?: (() => void) | undefined;
}): ReactNode {
  const kind = previewKind(attachment.mediaType, attachment.filename);
  const [zoomed, setZoomed] = useState(false);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [natural, setNatural] = useState<{ width: number; height: number } | null>(null);
  const dialogRef = useRef<HTMLDialogElement>(null);

  // A fresh file starts fresh: zoom and page belong to the previous one.
  useEffect(() => {
    setZoomed(false);
    setPage(1);
    setPages(1);
    setNatural(null);
  }, [attachment.id]);

  const pdfUrl = usePdfPage(kind === 'pdf' ? loaded?.blob ?? null : null, page, 1400);

  useEffect(() => {
    if (kind !== 'pdf' || loaded === null) return;
    void loaded.blob
      .arrayBuffer()
      .then(pdfPageCount)
      .then(setPages)
      .catch(() => setPages(1));
  }, [kind, loaded]);

  /*
   * A real `<dialog>`, opened modally.
   *
   * `showModal()` is not a stylistic choice: it puts the element in the browser's
   * top layer, centres it against the viewport rather than against whatever
   * ancestor happens to be positioned, traps focus, and closes on Escape. The
   * hand-built version did none of those for free and got the first two wrong —
   * it was laid out inside the scrolling content, so it drifted with the page
   * and ran off the bottom.
   */
  useEffect(() => {
    const dialog = dialogRef.current;
    if (dialog === null) return undefined;
    if (!dialog.open) dialog.showModal();
    // Mount-only, deliberately. Stepping to the next file keeps this same
    // element open — a cleanup keyed on the attachment would close it, and
    // the dialog's `close` event would then close the whole viewer.
    return () => {
      if (dialog.open) dialog.close();
    };
  }, []);

  /*
   * Arrow keys page through whatever has pages: a multi-page PDF steps its
   * own pages (staying inside the document a reader is in), everything else
   * steps between the card's files when there are neighbours to step to.
   */
  useEffect(() => {
    const pagesPdf = kind === 'pdf' && pages > 1;
    if (!pagesPdf && onPrev === undefined && onNext === undefined) return undefined;
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === 'ArrowRight') {
        if (pagesPdf) setPage((value) => Math.min(value + 1, pages));
        else onNext?.();
      }
      if (event.key === 'ArrowLeft') {
        if (pagesPdf) setPage((value) => Math.max(value - 1, 1));
        else onPrev?.();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [kind, pages, onPrev, onNext]);

  return (
    <dialog
      className={`viewer viewer-${kind}`}
      ref={dialogRef}
      aria-label={attachment.filename}
      /* Escape and the backdrop both route here, so there is one way out. */
      onClose={onClose}
      onClick={(event) => {
        // A click on the dialog element itself is a click on its padding — the
        // backdrop area — because the panel below fills the rest.
        if (event.target === dialogRef.current) onClose();
      }}
    >
      <div className="viewer-panel">
        <header className="viewer-head">
          <span className="viewer-title">{attachment.filename}</span>
          <span className="viewer-meta">
            {attachment.mediaType} · {formatBytes(attachment.sizeBytes)}
          </span>

          {position !== undefined && position.total > 1 ? (
            <span className="viewer-pager">
              <Button onClick={onPrev} disabled={onPrev === undefined} aria-label="Previous file">
                ‹
              </Button>
              <span className="viewer-page-count">
                {position.index + 1} / {position.total}
              </span>
              <Button onClick={onNext} disabled={onNext === undefined} aria-label="Next file">
                ›
              </Button>
            </span>
          ) : null}

          {kind === 'pdf' && pages > 1 ? (
            <span className="viewer-pager">
              <Button onClick={() => setPage((v) => Math.max(v - 1, 1))} disabled={page <= 1}>
                ‹
              </Button>
              <span className="viewer-page-count">
                {page} / {pages}
              </span>
              <Button onClick={() => setPage((v) => Math.min(v + 1, pages))} disabled={page >= pages}>
                ›
              </Button>
            </span>
          ) : null}

          {kind === 'image' ? (
            <Button onClick={() => setZoomed((value) => !value)}>
              {zoomed ? 'Fit' : 'Actual size'}
            </Button>
          ) : null}

          {loaded === null ? null : (
            <Button as="a" href={loaded.url} download={attachment.filename}>
              Download
            </Button>
          )}
          <Button onClick={onClose} autoFocus>
            Close
          </Button>
        </header>

        <div className="viewer-body">
          {loaded === null ? (
            <p className="viewer-status"><Orb size={20} /> Loading {attachment.filename}…</p>
          ) : kind === 'image' ? (
            <img
              className={zoomed ? 'viewer-img viewer-img-actual' : 'viewer-img'}
              src={loaded.url}
              alt={attachment.filename}
              onLoad={(event) =>
                setNatural({
                  width: event.currentTarget.naturalWidth,
                  height: event.currentTarget.naturalHeight,
                })
              }
              /*
               * Fit the dialog, enlarging a small image — but never past four
               * times its own pixels.
               *
               * Without an upper bound a 16px icon becomes a wall of blur that
               * misrepresents the file; without any enlargement a 96px
               * screenshot sat alone in the middle of a large empty dialog,
               * which is what this looked like. Four is enough for a small
               * capture to be legible and not enough to pretend detail that is
               * not in the file.
               */
              style={
                zoomed || natural === null
                  ? undefined
                  : { maxWidth: natural.width * 4, maxHeight: natural.height * 4 }
              }
              onClick={() => setZoomed((value) => !value)}
            />
          ) : kind === 'video' ? (
            <video className="viewer-media" src={loaded.url} controls />
          ) : kind === 'audio' ? (
            <audio className="viewer-audio" src={loaded.url} controls />
          ) : kind === 'pdf' ? (
            pdfUrl === null ? (
              <p className="viewer-status"><Orb size={20} /> Rendering page {page}…</p>
            ) : (
              <img className="viewer-page" src={pdfUrl} alt={`Page ${String(page)} of ${attachment.filename}`} />
            )
          ) : kind === 'text' ? (
            <CodeBlock text={loaded.text ?? ''} filename={attachment.filename} />
          ) : (
            <p className="viewer-status">
              This file has no preview. <a href={loaded.url} download={attachment.filename}>Download {attachment.filename}</a>.
            </p>
          )}
        </div>
      </div>
    </dialog>
  );
}
